import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  InputAdornment, 
  IconButton, 
  Divider, 
  LinearProgress,
  Avatar,
  Badge,
  Card,
  CardContent,
  Collapse,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Chip,
  Stack
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from '@mui/icons-material/Clear';

/**
 * Modern organization list component 
 */
const OrganizationList = ({
  organizations = [],
  totalCount = null,
  loading = false,
  searchQuery = '',
  onSearchChange,
  selectedOrganizationId,
  onOrganizationClick,
  typeFilter = 'all',
  onTypeFilterChange,
  regionFilter = 'all',
  onRegionFilterChange,
  availableRegions = [],
  postcodeFilter = '',
  onPostcodeFilterChange,
  consultantFilter = 'all',
  onConsultantFilterChange,
  availableConsultants = [],
}) => {
  // Filter panel open state
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  
  // Pagination state for performance
  const [displayLimit, setDisplayLimit] = useState(50);

  // Handle scroll to load more organizations
  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 100;
    if (bottom && displayLimit < organizations.length) {
      setDisplayLimit(prev => Math.min(prev + 50, organizations.length));
    }
  };

  // Reset display limit when any filter changes
  React.useEffect(() => {
    setDisplayLimit(50);
  }, [searchQuery, typeFilter, regionFilter, postcodeFilter, consultantFilter]);

  // Count active non-type filters
  const activeFilterCount = [
    regionFilter !== 'all',
    postcodeFilter !== '',
    consultantFilter !== 'all',
    typeFilter !== 'all',
  ].filter(Boolean).length;

  // Clear all filters
  const handleClearAllFilters = () => {
    onTypeFilterChange('all');
    onRegionFilterChange('all');
    onPostcodeFilterChange('');
    onConsultantFilterChange('all');
  };

  // Get visible organizations
  const visibleOrganizations = organizations.slice(0, displayLimit);

  // Count all orgs (pre-filter) by type — not needed for dynamic counts, keep for type chips
  const orgCounts = organizations.reduce((counts, org) => {
    if (org.isSchool || org.is_school) counts.schools++;
    else if (org.isBusiness || org.is_business) counts.businesses++;
    else counts.other++;
    return counts;
  }, { schools: 0, businesses: 0, other: 0 });

  // Helper function to get the organization type icon
  const getOrgTypeIcon = (org) => {
    const isSchool   = org.isSchool   || org.is_school;
    const isBusiness = org.isBusiness || org.is_business;
    if (isSchool && isBusiness) {
      return (
        <Avatar sx={{ bgcolor: '#1565C0', width: 32, height: 32 }}>
          <SchoolIcon sx={{ fontSize: 18 }} />
        </Avatar>
      );
    }
    if (isSchool) {
      return (
        <Avatar sx={{ bgcolor: '#E65100', width: 32, height: 32 }}>
          <SchoolIcon sx={{ fontSize: 18 }} />
        </Avatar>
      );
    }
    return (
      <Avatar sx={{ bgcolor: 'secondary.light', width: 32, height: 32 }}>
        <BusinessIcon sx={{ fontSize: 18 }} />
      </Avatar>
    );
  };

  // Helper function to get the status chip
  const getStatusChip = (org) => {
    const isSchool = org.isSchool || org.is_school;
    const isBusiness = org.isBusiness || org.is_business;
    
    // For schools (including school & business), prioritize schoolStatus
    if (isSchool && org.schoolStatus) {
      const status = org.schoolStatus;
      let bgcolor, color;
      
      switch (status) {
        case 'Active':
          bgcolor = 'success.light';
          color = 'success.dark';
          break;
        case 'Reorder':
          bgcolor = 'warning.light';
          color = 'warning.dark';
          break;
        case 'Inactive':
        default:
          bgcolor = 'grey.300';
          color = 'text.secondary';
          break;
      }
      
      return (
        <Box 
          component="span"
          sx={{ 
            px: 1,
            py: 0.5,
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 500,
            bgcolor,
            color
          }}
        >
          {status}
        </Box>
      );
    }
    
    // For businesses only (not schools), check Bad Debt and Business Closed
    if (isBusiness && !isSchool) {
      const hasBadDebt = org.badDebt || org.bad_debt;
      const isClosed = org.businessClosed || org.business_closed;
      const isInactive = hasBadDebt || isClosed;
      
      return (
        <Box 
          component="span"
          sx={{ 
            px: 1,
            py: 0.5,
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 500,
            bgcolor: isInactive ? 'grey.300' : 'success.light',
            color: isInactive ? 'text.secondary' : 'success.dark'
          }}
        >
          {isInactive ? 'Inactive' : 'Active'}
        </Box>
      );
    }
    
    // Fallback for other cases
    const isActive = org.isActive || org.is_active;
    return (
      <Box 
        component="span"
        sx={{ 
          px: 1,
          py: 0.5,
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 500,
          bgcolor: isActive ? 'success.light' : 'grey.300',
          color: isActive ? 'success.dark' : 'text.secondary'
        }}
      >
        {isActive ? 'Active' : 'Inactive'}
      </Box>
    );
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 0 }}>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight="600">
              Organisations <Typography component="span" variant="subtitle1" color="text.secondary" fontWeight={400}>
                ({totalCount !== null ? totalCount.toLocaleString() : organizations.length.toLocaleString()}
                {totalCount !== null && totalCount > organizations.length ? `, ${organizations.length.toLocaleString()} shown` : ''})
              </Typography>
            </Typography>
            {activeFilterCount > 0 && (
              <Chip
                label="Clear filters"
                size="small"
                variant="outlined"
                onDelete={handleClearAllFilters}
                deleteIcon={<ClearIcon />}
                sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
              />
            )}
          </Box>

          {/* Search + filter toggle */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search organisations..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchQuery && (
                    <IconButton size="small" onClick={() => onSearchChange('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => setFilterPanelOpen(p => !p)}
                    color={activeFilterCount > 0 ? 'primary' : 'default'}
                    sx={{ ml: 0.25 }}
                  >
                    <Badge badgeContent={activeFilterCount || null} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 14, height: 14 } }}>
                      <FilterListIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </InputAdornment>
              ),
              sx: { borderRadius: 2, fontSize: '0.875rem' }
            }}
            sx={{ mb: 1.5 }}
          />

          {/* Consultant quick-filter row */}
          {availableConsultants.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Assigned to
              </Typography>
              <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.75 }}>
                <Chip
                  size="small"
                  label="All"
                  onClick={() => onConsultantFilterChange('all')}
                  color={consultantFilter === 'all' ? 'primary' : 'default'}
                  variant={consultantFilter === 'all' ? 'filled' : 'outlined'}
                  sx={{ fontSize: '0.75rem', fontWeight: consultantFilter === 'all' ? 600 : 400 }}
                />
                {availableConsultants.map(c => {
                  const active = consultantFilter === c.id.toString();
                  return (
                    <Chip
                      key={c.id}
                      size="small"
                      avatar={
                        <Avatar sx={{ bgcolor: active ? 'primary.dark' : 'grey.400', width: 18, height: 18, fontSize: '0.65rem', fontWeight: 700 }}>
                          {c.name.charAt(0).toUpperCase()}
                        </Avatar>
                      }
                      label={c.name}
                      onClick={() => onConsultantFilterChange(c.id.toString())}
                      color={active ? 'primary' : 'default'}
                      variant={active ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.75rem', fontWeight: active ? 600 : 400 }}
                    />
                  );
                })}
                <Chip
                  size="small"
                  label="Unassigned"
                  onClick={() => onConsultantFilterChange('unassigned')}
                  color={consultantFilter === 'unassigned' ? 'warning' : 'default'}
                  variant={consultantFilter === 'unassigned' ? 'filled' : 'outlined'}
                  sx={{ fontSize: '0.75rem', fontWeight: consultantFilter === 'unassigned' ? 600 : 400 }}
                />
              </Stack>
            </Box>
          )}

          {/* Collapsible extra filters */}
          <Collapse in={filterPanelOpen}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 1.5, pt: 0.5 }}>

              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={typeFilter} label="Type" onChange={(e) => onTypeFilterChange(e.target.value)}>
                  <MenuItem value="all">All Types ({totalCount !== null ? totalCount.toLocaleString() : organizations.length})</MenuItem>
                  <MenuItem value="school">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <SchoolIcon fontSize="small" />Schools ({orgCounts.schools})
                    </Box>
                  </MenuItem>
                  <MenuItem value="business">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <BusinessIcon fontSize="small" />Businesses ({orgCounts.businesses})
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {availableRegions.length > 0 && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Region</InputLabel>
                  <Select value={regionFilter} label="Region" onChange={(e) => onRegionFilterChange(e.target.value)}>
                    <MenuItem value="all">All Regions</MenuItem>
                    {availableRegions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Select>
                </FormControl>
              )}

              <TextField
                size="small"
                fullWidth
                label="Postcode"
                placeholder="e.g. 3000"
                value={postcodeFilter}
                onChange={(e) => onPostcodeFilterChange(e.target.value.replace(/\D/g, ''))}
                InputProps={{
                  endAdornment: postcodeFilter ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => onPostcodeFilterChange('')}><ClearIcon fontSize="small" /></IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />

              {/* Active filter summary chips */}
              {(typeFilter !== 'all' || regionFilter !== 'all' || postcodeFilter) && (
                <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
                  {typeFilter !== 'all' && (
                    <Chip size="small" label={typeFilter === 'school' ? 'Schools only' : 'Businesses only'} onDelete={() => onTypeFilterChange('all')} />
                  )}
                  {regionFilter !== 'all' && (
                    <Chip size="small" label={regionFilter} onDelete={() => onRegionFilterChange('all')} />
                  )}
                  {postcodeFilter && (
                    <Chip size="small" label={`Postcode: ${postcodeFilter}`} onDelete={() => onPostcodeFilterChange('')} />
                  )}
                </Stack>
              )}
            </Box>
          </Collapse>

        </Box>
        
        {loading && <LinearProgress />}
        
        <Divider />
        
        <Box 
          sx={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}
          onScroll={handleScroll}
        >
          {organizations.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No organisations found
              </Typography>
            </Box>
          ) : (
            <>
              {visibleOrganizations.map((org) => (
              <Box
                key={org.id}
                onClick={() => onOrganizationClick(org)}
                sx={{
                  px: 2, py: 1.5,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  backgroundColor: selectedOrganizationId === org.id ? 'rgba(25,118,210,0.07)' : 'transparent',
                  borderLeft: selectedOrganizationId === org.id ? '3px solid #1976d2' : '3px solid transparent',
                  '&:hover': {
                    backgroundColor: selectedOrganizationId === org.id ? 'rgba(25,118,210,0.07)' : 'rgba(0,0,0,0.03)',
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  borderBottom: '1px solid rgba(0,0,0,0.06)',
                }}
              >
                {/* Type icon */}
                {getOrgTypeIcon(org)}

                {/* Main content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Row 1: name + bad debt badge */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      noWrap
                      sx={{ color: selectedOrganizationId === org.id ? 'primary.main' : 'text.primary' }}
                    >
                      {org.name}
                    </Typography>
                    {(org.badDebt || org.bad_debt) && (org.isBusiness || org.is_business) && (
                      <Chip label="Bad Debt" size="small" color="error" variant="outlined"
                        sx={{ height: 16, fontSize: '0.65rem', px: 0.25 }} />
                    )}
                  </Box>

                  {/* Row 2: location · status · consultant */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, flexWrap: 'wrap' }}>
                    {(org.suburb || org.postcode) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                        <LocationOnIcon sx={{ fontSize: '0.75rem', color: 'text.disabled', flexShrink: 0 }} />
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {[org.suburb, org.postcode].filter(Boolean).join(' ')}
                        </Typography>
                      </Box>
                    )}
                    {getStatusChip(org)}
                    {org.salesConsultantName && (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer',
                              bgcolor: 'grey.100', borderRadius: 10, px: 0.75, py: 0.2,
                              '&:hover': { bgcolor: 'primary.50' } }}
                        onClick={(e) => { e.stopPropagation(); onConsultantFilterChange(org.sales_consultant_id?.toString()); }}
                      >
                        <Avatar sx={{ width: 14, height: 14, fontSize: '0.55rem', bgcolor: 'primary.main', flexShrink: 0 }}>
                          {org.salesConsultantName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1, fontWeight: 500 }}>
                          {org.salesConsultantName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              ))}
              
              {displayLimit < organizations.length && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {displayLimit} of {organizations.length} organisations
                    <br />
                    <Typography variant="caption">
                      Scroll down to load more
                    </Typography>
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default OrganizationList; 